import { Component, computed, DestroyRef, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormControlStatus, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { KENDO_BUTTONS, KENDO_SPLITBUTTON } from '@progress/kendo-angular-buttons';
import { DropDownListComponent, KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_FORMFIELD, KENDO_TEXTBOX } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { ClearableDropdownlistDirective } from '@unity/directives/clearable-dropdownlist.directive';
import { ControlWrapperComponent } from '@unity/forms/composed/control-wrapper/control-wrapper.component';
import { SortPipe } from '@unity/pipes/sort.pipe';
import { IconService } from '@unity/services/icon.service';
import { SortConfig } from '@unity/utils/types';

@Component({
  selector: 'unity-dropdown-clickable-control',
  standalone: true,
  imports: [
    KENDO_DROPDOWNS,
    KENDO_TEXTBOX,
    KENDO_SPLITBUTTON,
    KENDO_LOADER,
    KENDO_LABEL,
    KENDO_FORMFIELD,
    KENDO_BUTTONS,
    ReactiveFormsModule,
    RouterLink,
    SortPipe,
    ClearableDropdownlistDirective,
    ControlWrapperComponent,
  ],
  templateUrl: './dropdown-clickable-control.component.html',
  styleUrl: './dropdown-clickable-control.component.scss',
})
export class DropdownClickableControlComponent<T> implements OnInit {
  readonly #destroy = inject(DestroyRef);
  private readonly iconService = inject(IconService);

  private readonly $dropdownlistReference = viewChild.required<DropDownListComponent>('dropdownlist');

  private readonly $controlStatus = signal<FormControlStatus>('PENDING');

  protected xCircleIcon = this.iconService.xCircleIcon;
  protected readonly $isDisabled = computed(() => this.$controlStatus() == 'DISABLED');

  public readonly $selectable = input.required<T[] | undefined>({ alias: 'selectable' });
  public readonly $linkRoot = input.required<string>({ alias: 'linkRoot' });

  public readonly $control = input.required<FormControl<number | null>>({ alias: 'control' });
  public readonly $displayName = input.required<string>({ alias: 'displayName' });
  public readonly $description = input.required<string>({ alias: 'description' });

  public readonly $showHint = input(true, { alias: 'showHint' });
  public readonly $currentSelection = input<T>(undefined, { alias: 'currentSelection' });
  public readonly $textField = input('name', { alias: 'textField' });
  public readonly $linkField = input('id', { alias: 'linkField' });
  public readonly $readonly = input(false, { alias: 'readonly' });
  public readonly $sortBy = input<string>(undefined, { alias: 'sortBy' });

  public selectionChangeEvent = output<T>();

  protected readonly $defaultSortCfg = computed<SortConfig[]>(() => [
    {
      field: this.$sortBy() ?? this.$textField(),
      dir: 'asc',
    },
  ]);

  public ngOnInit() {
    // ReactiveForm observable -> signal interop is nonexistant as of this writing.
    // TODO: improve this once better resources are available.
    const control = this.$control();
    control.statusChanges.pipe(takeUntilDestroyed(this.#destroy)).subscribe(value => this.$controlStatus.set(value));
  }

  protected onSelectionChange(event: T) {
    this.selectionChangeEvent.emit(event);
  }

  protected reset() {
    const control = this.$control();
    // 1. Reset to default (sets pristine)
    control.reset();

    if (control.value !== null) {
      // 2. Explicitly set to null and mark dirty if default is not null
      control.setValue(null);
      control.markAsDirty();
    }
    // 3. Ensure dropdown is closed
    this.$dropdownlistReference().toggle(false);
  }
}
